/**
 * Local sessions on a node (D20; `@norna/api-contract/offlineAuth.js`): what the
 * SPA sends to a node once it has one, so an internet outage mid-show never
 * signs a working operator out.
 *
 * - **Exchange**: right after a Cognito (or dev) sign-in against a node, the
 *   token is exchanged for a local session (`POST /local/session`) — signed
 *   by the site key, so the peer node accepts it too after a failover.
 * - **Break-glass**: `POST /local/login {account, secret}` — an offline PIN
 *   or the site's emergency account, accepted only while the node cannot
 *   reach the cloud.
 * - **Renewal**: `POST /local/session` with the local session itself (12 h,
 *   sliding): due once older than `LOCAL_SESSION_RENEW_AFTER_SECONDS`
 *   (`renewalDue` in `session.ts`). A 401 is a refusal (revoked: sign in again); a
 *   network failure or a 5xx is transient — while the token is still valid
 *   the session simply stands, and only a token about to expire fails the
 *   request.
 *
 * The node's answers are read tolerantly (D37): break-glass happens during an
 * outage, when the node (held on a pin) and this SPA (cached) are most likely
 * on different releases — a field a newer node added must not refuse sign-in.
 */
import type { FetchPort } from '@norna/api-client/ports.js';
import { localSessionResponseWireSchema, type LocalSessionResponseWire } from '@norna/api-contract/offlineAuth.js';
import { parseContract } from '@norna/api-contract/parsing.js';
import { REFRESH_SKEW_MS, type Session } from './session.js';

/** A refusal of `POST /local/login`, with the node's reason and (when locked or rate-limited) how long to wait. */
export class LocalLoginError extends Error {
  readonly status: number;
  readonly retryAfterSeconds: number | null;
  constructor(message: string, status: number, retryAfterSeconds: number | null) {
    super(message);
    this.name = 'LocalLoginError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export function sessionFromLocal(response: LocalSessionResponseWire): Session {
  return {
    provider: 'local',
    idToken: response.token,
    accessToken: null,
    refreshToken: null,
    userId: response.userId,
    tenantId: response.tenantId,
    email: response.email,
    expiresAtMs: Date.parse(response.expiresAt),
  };
}

type Answer = { readonly status: number; readonly json: unknown };

async function post(fetch: FetchPort, url: string, token: string | null, body: unknown): Promise<Answer> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (token !== null) headers['authorization'] = `Bearer ${token}`;
  if (body !== null) headers['content-type'] = 'application/json';
  const response = await fetch(url, { method: 'POST', headers, body: body === null ? null : JSON.stringify(body) });
  const text = await response.text();
  let json: unknown = null;
  try {
    json = text === '' ? null : JSON.parse(text);
  } catch {
    json = null;
  }
  return { status: response.status, json };
}

function errorField(json: unknown, name: string): unknown {
  const error = typeof json === 'object' && json !== null ? Reflect.get(json, 'error') : undefined;
  return typeof error === 'object' && error !== null ? Reflect.get(error, name) : undefined;
}

function errorMessageOf(json: unknown): string | null {
  const message = errorField(json, 'message');
  return typeof message === 'string' ? message : null;
}

function refusalMessage(status: number, nodeMessage: string | null, retryAfterSeconds: number | null): string {
  if (status === 401) return 'Wrong account or secret.';
  if (status === 403) return nodeMessage ?? 'Offline sign-in is only possible while this node cannot reach the cloud.';
  if (status === 429) return `Too many attempts${nodeMessage === null ? '' : ` (${nodeMessage})`}. Try again in ${String(retryAfterSeconds ?? 60)} s.`;
  return nodeMessage ?? `Offline sign-in failed (HTTP ${String(status)}).`;
}

/** Break-glass sign-in. Throws `LocalLoginError` on a refusal. */
export async function localLogin(fetch: FetchPort, apiUrl: string, account: string, secret: string): Promise<Session> {
  const answer = await post(fetch, `${apiUrl}/local/login`, null, { account, secret });
  if (answer.status === 200) {
    const parsed = parseContract(localSessionResponseWireSchema, answer.json, 'tolerant');
    if (!parsed.success) throw new LocalLoginError('The node answered offline sign-in with something that is not a session.', 200, null);
    return sessionFromLocal(parsed.data);
  }
  // The contract's error body carries the wait (`retryAfterSeconds`) for a lockout or the address's rate limit.
  const retry = errorField(answer.json, 'retryAfterSeconds');
  const retryAfterSeconds = typeof retry === 'number' ? retry : null;
  throw new LocalLoginError(refusalMessage(answer.status, errorMessageOf(answer.json), retryAfterSeconds), answer.status, retryAfterSeconds);
}

/**
 * Exchange a Cognito/dev token (or renew a local session): the new local session; null when refused (401) or the
 * node cannot issue one (an older node without `/local/session`, no site key yet); throws on a transient failure.
 */
export async function exchangeForLocalSession(fetch: FetchPort, apiUrl: string, token: string): Promise<Session | null> {
  const answer = await post(fetch, `${apiUrl}/local/session`, token, null);
  if (answer.status === 200) {
    const parsed = parseContract(localSessionResponseWireSchema, answer.json, 'tolerant');
    return parsed.success ? sessionFromLocal(parsed.data) : null;
  }
  if (answer.status >= 500 && answer.status !== 503) throw new Error(`the node could not renew the session (HTTP ${String(answer.status)})`);
  return null;
}

/**
 * The session store's refresher for a local session: renewed; null when the node refuses it (revoked — sign in
 * again); the session as it is while a renewal fails transiently but the token has life left; a rejection only
 * when it is about to expire.
 */
export function refreshLocalSession(fetch: FetchPort, apiUrl: () => string, nowMs: () => number): (session: Session) => Promise<Session | null> {
  return async (session) => {
    const stillValid = session.expiresAtMs - REFRESH_SKEW_MS > nowMs();
    let answer: Answer;
    try {
      answer = await post(fetch, `${apiUrl()}/local/session`, session.idToken, null);
    } catch (err) {
      if (stillValid) return session;
      throw err;
    }
    if (answer.status === 200) {
      const parsed = parseContract(localSessionResponseWireSchema, answer.json, 'tolerant');
      if (parsed.success) return sessionFromLocal(parsed.data);
    }
    if (answer.status === 401) return null;
    if (stillValid) return session;
    throw new Error(`the node could not renew the session (HTTP ${String(answer.status)})`);
  };
}
